// RelatedCross — cross-section internal-linking block.
// Shown on blog & service detail pages. Three columns: Services / Articles / Industries.
import { Link } from "@tanstack/react-router";
import { Briefcase, BookOpen, Factory, ArrowRight } from "lucide-react";
import type { RelatedBundle } from "@/lib/related";

type Theme = "dark" | "light";

export function RelatedCross({
  bundle,
  theme = "light",
  heading = "เนื้อหาที่เกี่ยวข้อง · เพิ่มเติม",
  sub = "เชื่อมโยงระหว่างบริการ บทความ และอุตสาหกรรม เพื่อช่วยให้คุณตัดสินใจได้เร็วขึ้น",
}: {
  bundle: RelatedBundle;
  theme?: Theme;
  heading?: string;
  sub?: string;
}) {
  const { services, posts, industries } = bundle;
  const hasAny = services.length + posts.length + industries.length > 0;
  if (!hasAny) return null;

  const dark = theme === "dark";
  const cardCls = dark
    ? "border-white/10 bg-white/[0.04] hover:border-gold/40 hover:bg-white/[0.07]"
    : "border-slate-200 bg-white hover:border-amber-400/50 hover:shadow-md";
  const titleCls = dark ? "text-white" : "text-navy-deep";
  const subCls = dark ? "text-white/80" : "text-slate-600";
  const headSection = dark ? "text-gold" : "text-amber-600";
  const sectionWrap = dark ? "" : "border-t border-slate-200 bg-slate-50/60";

  return (
    <section className={`${sectionWrap} mt-14 px-0 py-12 sm:py-14`}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="mb-8 flex flex-col gap-1.5">
          <div className={`text-[11px] font-bold uppercase tracking-widest ${headSection}`}>
            Internal Knowledge Graph
          </div>
          <h2 className={`font-display text-2xl font-extrabold sm:text-3xl ${titleCls}`}>{heading}</h2>
          <p className={`max-w-2xl text-sm ${subCls}`}>{sub}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {services.length > 0 && (
            <Column
              icon={<Briefcase className="h-4 w-4" />}
              label="บริการที่เกี่ยวข้อง"
              theme={theme}
            >
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$service"
                  params={{ service: s.slug }}
                  className={`group block rounded-xl border p-3.5 transition ${cardCls}`}
                >
                  <div className={`text-[13px] font-bold leading-snug ${titleCls} group-hover:text-amber-600`}>
                    {s.nameTh}
                  </div>
                  <div className={`mt-1 text-[11px] ${subCls}`}>
                    ฿{s.priceFrom.toLocaleString()} · {s.turnaround}
                  </div>
                </Link>
              ))}
            </Column>
          )}

          {posts.length > 0 && (
            <Column
              icon={<BookOpen className="h-4 w-4" />}
              label="บทความที่เกี่ยวข้อง"
              theme={theme}
            >
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className={`group block rounded-xl border p-3.5 transition ${cardCls}`}
                >
                  <div className={`text-[10px] font-bold uppercase tracking-widest ${headSection}`}>{p.category}</div>
                  <div className={`mt-1 text-[13px] font-bold leading-snug ${titleCls} group-hover:text-amber-600`}>
                    {p.title}
                  </div>
                  <div className={`mt-1 text-[11px] ${subCls}`}>{p.readMinutes} นาที</div>
                </Link>
              ))}
            </Column>
          )}

          {industries.length > 0 && (
            <Column
              icon={<Factory className="h-4 w-4" />}
              label="อุตสาหกรรมที่เกี่ยวข้อง"
              theme={theme}
            >
              {industries.map((i) => (
                <Link
                  key={i.slug}
                  to="/industries/$slug"
                  params={{ slug: i.slug }}
                  className={`group block rounded-xl border p-3.5 transition ${cardCls}`}
                >
                  <div className={`text-[13px] font-bold leading-snug ${titleCls} group-hover:text-amber-600`}>
                    {i.name}
                  </div>
                  <div className={`mt-1 text-[11px] ${subCls}`}>{i.tagline}</div>
                </Link>
              ))}
              <Link
                to="/industries"
                className={`inline-flex items-center gap-1 px-1 pt-1 text-[12px] font-bold ${dark ? "text-gold" : "text-amber-600"}`}
              >
                ดู Industry Hub ทั้งหมด <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Column>
          )}
        </div>
      </div>
    </section>
  );
}

function Column({
  icon, label, theme, children,
}: { icon: React.ReactNode; label: string; theme: Theme; children: React.ReactNode }) {
  const dark = theme === "dark";
  return (
    <div className="space-y-2.5">
      <div className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest ${dark ? "text-white/75" : "text-slate-700"}`}>
        <span className={`flex h-6 w-6 items-center justify-center rounded-md ${dark ? "bg-gold/15 text-gold" : "bg-amber-100 text-amber-700"}`}>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </div>
  );
}
